/**
 * Экран несостоявшейся публикации. 09 §9.3 — норма, приоритет над `05-ui.md`.
 *
 * Четыре правила этого файла, каждое из них — решение, а не стиль:
 *
 * 1. **Набор кнопок и строка «что точно не произошло» выбираются по `status` ВЕРХНЕГО
 *    уровня**, а не по `publication.job_state`. Состояние работы и состояние версии
 *    расходятся законно (работа `cancelled` при версии `approved`, работа `sent` при версии
 *    `needs_manual_check`), и человеку нужно то, что можно сделать с ВЕРСИЕЙ.
 * 2. **Кнопки «повторить» нет ни в одном состоянии.** Ретраев в системе не существует
 *    (решение 22.08): ключ либо сожжён nonce'ом, либо мёртв по состоянию, а слепой повтор
 *    даёт дубль в брендовом канале. Новый слот — это новое одобрение человеком и новый ключ.
 * 3. **Проверку `post_url` UI не дублирует.** Разбор ссылки и сверку канала делает бэкенд
 *    (`post_url_unparsable`, `post_url_channel_mismatch`); здесь показывается его ответ.
 *    Вторая регулярка на клиенте — это второй источник правды о форме ссылки.
 * 4. **Пустого блока «что произошло» быть не должно.** Незнакомый `outcome_code` даёт
 *    запасную фразу (`phrases.ts`), потому что молчание экран читает как «система не знает».
 */

import { type JSX, useState } from 'react';
import { ApiRequestError, post } from '../../api/client.ts';
import type {
  ConfirmNotPublishedResponse,
  ConfirmPublishedResponse,
  Publication,
  RenderModel,
  ReopenResponse,
} from '../../api/types.ts';
import { stampMsk } from '../../components/format.ts';
import { IconExternal } from '../../components/icons.tsx';
import { outcomePhrase } from './phrases.ts';

/**
 * `https://t.me/c/<chat_id без префикса -100>[/<message_id>]` — 09 §9.3 дословно.
 * `CHAT_ID_RE` гарантирует префикс `-100`, поэтому срез фиксированный, без разбора.
 */
export function channelUrl(chatId: string, messageId: number | null): string {
  const internal = chatId.startsWith('-100') ? chatId.slice(4) : chatId.replace(/^-/, '');
  return messageId === null
    ? `https://t.me/c/${internal}`
    : `https://t.me/c/${internal}/${messageId}`;
}

/** Строка свидетельства автоматической проверки — 09 §9.3, четыре ветки. */
export function resolutionLine(p: Publication): string {
  const r = p.resolution;
  if (r === null) {
    return 'Автоматическая проверка ещё не закрыта — дождитесь её результата или проверьте канал сами.';
  }
  if (r.result_kind === 'found') {
    return `Автоматическая проверка нашла в канале пост №${String(r.found_message_id)}. Подтвердите, что это он.`;
  }
  if (r.result_kind === 'not_found') {
    return 'Автоматическая проверка пост не нашла. Проверьте канал сами.';
  }
  return `Автоматическая проверка недоступна: ${r.detail ?? 'причина не указана'}.`;
}

const NOT_SENT = 'Пост в канал не отправлен. Подписчики его не видели.';

export interface FailedPanelProps {
  readonly model: RenderModel;
  readonly onChanged: () => void;
  /** Открывает диалог утверждения: переход 25 — новый слот, новый ключ, новый nonce. */
  readonly onApproveNewSlot: () => void;
}

export function FailedPanel({ model, onChanged, onApproveNewSlot }: FailedPanelProps): JSX.Element {
  const publication = model.publication;
  const [postUrl, setPostUrl] = useState<string>(() => {
    const r = publication?.resolution;
    if (publication === null || r === null || r === undefined || r.result_kind !== 'found') {
      return '';
    }
    // Ветка `found`: поле предзаполняется найденным номером — человек подтверждает, а не
    // переписывает ссылку руками (09 §9.3).
    return channelUrl(publication.chat_id, r.found_message_id);
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (publication === null) {
    return (
      <div className="stack" style={{ gap: 12 }}>
        <span className="eyebrow">Публикация не состоялась</span>
        <div className="val val-err">
          <span>
            Работы публикации у этой версии нет, хотя её состояние — «{model.status}». Это
            расхождение данных: покажите экран инженеру, сами ничего не нажимайте.
          </span>
        </div>
      </div>
    );
  }

  const act = (path: string, body: Record<string, unknown>, onOk: () => void = onChanged): void => {
    if (busy) return;
    setBusy(true);
    setError(null);
    post<ConfirmPublishedResponse | ConfirmNotPublishedResponse | ReopenResponse>(
      `/api/v1/drafts/${model.draft_id}/versions/${model.version_no}/${path}`,
      body,
    )
      .then(() => {
        onOk();
      })
      .catch((e: unknown) => {
        if (e instanceof ApiRequestError && e.code === 'post_url_unparsable') {
          setError(`${e.message}. Скопируйте ссылку в канале через «Copy Post Link».`);
        } else if (e instanceof ApiRequestError && e.code === 'post_url_channel_mismatch') {
          setError(`${e.message}: этот номер относится к другому каналу.`);
        } else if (e instanceof ApiRequestError && e.status === 409) {
          setError(`${e.message}. Обновите экран.`);
          onChanged();
        } else {
          setError(e instanceof Error ? e.message : String(e));
        }
      })
      .finally(() => {
        setBusy(false);
      });
  };

  const unknownOutcome = model.status === 'needs_manual_check';
  const link = channelUrl(publication.chat_id, publication.telegram_message_id);

  return (
    <div className="stack" style={{ gap: 12 }}>
      <span className="eyebrow">
        {model.status === 'publishing' ? 'Публикация идёт' : 'Публикация не состоялась'}
      </span>

      <div className="val val-err" style={{ alignItems: 'flex-start' }}>
        <span>{outcomePhrase(publication.outcome_code, publication.outcome_detail)}</span>
      </div>

      <div className="card-flat stack" style={{ gap: 10 }}>
        <div className="h3">Что точно не произошло</div>
        <div className="small">
          {model.status === 'needs_manual_check'
            ? 'Неизвестно, вышел ли пост. Проверьте канал или дождитесь автоматической проверки.'
            : model.status === 'publishing'
              ? 'Пока ничего не решено: публикатор ещё работает с этим постом. Кнопки решения появятся, когда исход будет записан.'
              : model.status === 'expired'
                ? `Слот прошёл, ключ мёртв. ${NOT_SENT}`
                : NOT_SENT}
        </div>
        {unknownOutcome ? <div className="small">{resolutionLine(publication)}</div> : null}
        {unknownOutcome ? (
          <a
            className="btn btn-ghost btn-sm"
            href={link}
            target="_blank"
            rel="noreferrer noopener"
            style={{ alignSelf: 'flex-start', textDecoration: 'none' }}
          >
            <IconExternal /> Открыть канал
          </a>
        ) : null}
      </div>

      {error === null ? null : (
        <div className="val val-err">
          <span>{error}</span>
        </div>
      )}

      {/* ── что можно сделать: набор строго по status верхнего уровня ── */}

      {model.status === 'needs_manual_check' ? (
        <div className="card-flat stack" style={{ gap: 10 }}>
          <div className="h3">Что вы увидели в канале</div>
          <label className="lbl" htmlFor="post-url" style={{ margin: 0 }}>
            Ссылка на пост — «Copy Post Link» в Telegram
          </label>
          <input
            id="post-url"
            className="inp"
            value={postUrl}
            placeholder="https://t.me/c/1234567890/1205"
            onChange={(e) => {
              setPostUrl(e.target.value);
            }}
          />
          <div className="micro">
            Форму ссылки и принадлежность каналу проверяет сервер: номер сообщения из чужого канала
            записать в эту версию нельзя.
          </div>
          <button
            type="button"
            className={postUrl.trim() === '' || busy ? 'btn btn-w btn-dis' : 'btn btn-pri btn-w'}
            disabled={postUrl.trim() === '' || busy}
            onClick={() => {
              act('confirm-published', { post_url: postUrl.trim() });
            }}
          >
            Пост есть в канале
          </button>
          <button
            type="button"
            className="btn btn-ghost btn-w"
            disabled={busy}
            onClick={() => {
              act('confirm-not-published', {});
            }}
          >
            Поста нет в канале
          </button>
        </div>
      ) : null}

      {model.status === 'failed' ? (
        <div className="card-flat stack" style={{ gap: 10 }}>
          <div className="h3">Что можно сделать</div>
          <div className="micro">
            Повтора той же отправки не существует: ключ израсходован. Новый слот — это новое
            утверждение с удержанием и новый ключ.
          </div>
          <button type="button" className="btn btn-pri btn-w" onClick={onApproveNewSlot}>
            Утвердить на новый слот…
          </button>
          <button
            type="button"
            className="btn btn-ghost btn-w"
            disabled={busy}
            onClick={() => {
              act('reopen', {});
            }}
          >
            Вернуть в работу
          </button>
        </div>
      ) : null}

      {model.status === 'expired' ? (
        <div className="card-flat stack" style={{ gap: 10 }}>
          <div className="h3">Что можно сделать</div>
          <div className="micro">
            Из состояния «истёк» ключ не выпускается: сначала версия возвращается в очередь, и уже
            оттуда утверждается на новый слот.
          </div>
          <button
            type="button"
            className="btn btn-pri btn-w"
            disabled={busy}
            onClick={() => {
              act('reopen', {});
            }}
          >
            Вернуть в работу
          </button>
        </div>
      ) : null}

      {/* Технический след — под сгибом: это материал для инженера, а не для решения
          (§Ключ 7.5, последний абзац). */}
      {publication.audit === null ? null : (
        <details className="card-flat">
          <summary className="h3" style={{ cursor: 'pointer' }}>
            Технический след
          </summary>
          <div className="stack" style={{ gap: 4, marginTop: 8 }}>
            <span className="meta">попытка {publication.audit.attempt_id}</span>
            <span className="meta">nonce {publication.audit.nonce ?? '—'}</span>
            {/* Момент закрытия попытки печатался сырым ISO — одно из двух таких мест во всём
                UI (правка 3.1). Формат берётся из общего дома `components/format.ts`: своей
                нарезки времени здесь нет и заводить её нельзя. */}
            <span className="meta">
              закрыта{' '}
              {publication.audit.finished_at === null
                ? '—'
                : stampMsk(publication.audit.finished_at)}
            </span>
            <span className="meta">
              отказ {publication.audit.failure_code ?? '—'}
              {publication.audit.failure_description === null
                ? ''
                : ` · ${publication.audit.failure_description}`}
            </span>
            <span className="meta">работа {publication.job_state}</span>
          </div>
        </details>
      )}
    </div>
  );
}
